import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { FileSignature, Loader2, X, AlertTriangle, ShieldCheck } from "lucide-react";

/**
 * Iter 101 / Iter 105 — Contract-formation modal launched from the streamer's
 * chat view. Streamer enters per-category wager amounts AGREED with the
 * player; the server computes `agreed_weighted = Σ(category × coefficient)`
 * using the SAME onboarding coefficients (single source, fetched from
 * `/config/contract-categories/{vertical}`). Bounds are 70%–200% of the
 * player's verified 3-month WEIGHTED volume (weighted-vs-weighted). Reserve
 * X is re-quoted server-side on every keystroke (single source of truth —
 * no client-side fee math). NEVER exposes VG fee internals.
 *
 * Case-vertical operators fall back to a "not configured" state because
 * case categories for contract formation are legally gated (deliberately
 * NOT defaulting to casino weights).
 */
const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—");

// Human labels for the six casino keys. Order matches WAGER_CATEGORY_KEYS.
const CATEGORY_LABELS = {
  slots: "Slots",
  sports: "Sports",
  live_casino: "Live Casino",
  table_games: "Table Games",
  house_originals: "House / Originals",
  dice: "Dice",
};

export default function CreateContractModal({ chatId, onClose, onCreated }) {
  const { t } = useI18n();
  const [ctx, setCtx] = useState(null);
  const [config, setConfig] = useState(null);      // /config/contract-categories/{vertical}
  const [busy, setBusy] = useState(true);
  const [cats, setCats] = useState({});            // { slots: "10000", ... } (strings for controlled inputs)
  const [casino, setCasino] = useState("");        // Iter 106
  const [affCode, setAffCode] = useState("");      // Iter 106
  const [refLink, setRefLink] = useState("");      // Iter 106
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadCtx = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/chats/${chatId}/contract-formation`);
      setCtx(data);
      // Load the vertical-aware category config (single source of truth for coefficients).
      const { data: cfg } = await api.get(`/config/contract-categories/${encodeURIComponent(data.vertical)}`);
      setConfig(cfg);
      // Initialise inputs to 0 for every category so the user has a starting shape.
      if (cfg?.configured) {
        const init = {};
        (cfg.categories || []).forEach((c) => { init[c.key] = ""; });
        setCats(init);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
      onClose();
    } finally { setBusy(false); }
  }, [chatId, onClose]);

  useEffect(() => { loadCtx(); }, [loadCtx]);

  // Re-quote server-side on every category change (single source for both
  // agreed_weighted AND reserve X). Debounced-by-render — React batches keystrokes.
  useEffect(() => {
    if (!ctx || !config?.configured) return;
    const numeric = {};
    let anyPositive = false;
    Object.entries(cats).forEach(([k, v]) => {
      const n = Number(v || 0);
      const clean = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
      numeric[k] = clean;
      if (clean > 0) anyPositive = true;
    });
    if (!anyPositive) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    api.get(`/chats/${chatId}/contract-formation`, {
      params: { wagerCategoriesJson: JSON.stringify(numeric) },
    })
      .then(({ data }) => { if (!cancelled) setQuote(data); })
      .catch(() => { if (!cancelled) setQuote(null); })
      .finally(() => { if (!cancelled) setQuoting(false); });
    return () => { cancelled = true; };
  }, [cats, chatId, ctx, config]);

  // Local mirror of the weighted computation for immediate feedback while
  // the round-trip is in flight. Server is still authoritative for submit.
  const localWeighted = useMemo(() => {
    if (!config?.configured) return 0;
    let sum = 0;
    (config.categories || []).forEach((c) => {
      const amt = Number(cats[c.key] || 0);
      if (Number.isFinite(amt) && amt > 0) sum += amt * Number(c.coefficient || 0);
    });
    return Math.round(sum * 100) / 100;
  }, [cats, config]);

  const agreedWeighted = quote?.agreedWeightedWager ?? localWeighted;
  const inBounds = quote?.inBounds ?? (agreedWeighted >= (ctx?.minAgreed || 0) && agreedWeighted <= (ctx?.maxAgreed || 0));
  const reserveAmount = quote?.reserveAmount ?? null;
  // Iter 164 — PRIVACY FIX: the player rebate is a VG↔player matter and
  // is no longer exposed on the streamer endpoint. The tile and the two
  // reads (rebateRatePct / rebateExpectedAmount) were removed. Player-
  // side surfaces (RebatePreview, Player Agreement, PlayerIter3Settlements)
  // are unaffected — they call separate player-role endpoints.
  const affiliateValid = casino.trim().length > 0 && affCode.trim().length > 0;
  const refLinkValid = !refLink.trim() || /^https?:\/\//i.test(refLink.trim());
  const canSubmit = Boolean(quote?.canSubmit && affiliateValid && refLinkValid && !submitting && !quoting);
  const insufficient = Boolean(quote?.insufficientCredits);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const numeric = {};
      Object.entries(cats).forEach(([k, v]) => {
        const n = Number(v || 0);
        numeric[k] = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
      });
      const { data } = await api.post(`/chats/${chatId}/create-contract`, {
        wagerCategories: numeric,
        casino: casino.trim(),
        affiliateCode: affCode.trim(),
        referralLink: refLink.trim() || null,
      });
      toast.success("Contract created — awaiting player confirmation");
      onCreated?.(data);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object" && d.message) || apiErrorMessage(e);
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="create-contract-modal">
      <div className="w-full max-w-xl bg-[#0B1A14] border border-[#2DD4A7]/40 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
          <div className="flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-[#2DD4A7]" />
            <h3 className="font-display font-bold text-base text-[#E8ECF4]">Create contract</h3>
          </div>
          <button data-testid="cc-close" onClick={onClose} className="text-[#8B97AE] hover:text-[#E8ECF4]"><X className="w-4 h-4" /></button>
        </div>

        {busy || !ctx ? (
          <div className="p-8 flex items-center justify-center gap-2 text-xs text-[#8B97AE]"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : ctx.existingContract ? (
          <div className="p-6 space-y-3" data-testid="cc-existing">
            <div className="flex items-center gap-2 text-[#F5B841]"><AlertTriangle className="w-4 h-4" /><span className="text-sm font-display font-bold">Contract already in progress</span></div>
            <p className="text-xs text-[#8B97AE]">A contract with this player is currently <span className="font-mono uppercase text-[#F5B841]">{ctx.existingContract.status}</span>. You can start a new one once it settles or ends.</p>
            <div className="pt-2 flex justify-end">
              <button onClick={onClose} className="px-4 h-9 border border-white/20 text-xs font-mono uppercase tracking-widest hover:bg-white/5">Close</button>
            </div>
          </div>
        ) : !config?.configured ? (
          <div className="p-6 space-y-3" data-testid="cc-not-configured">
            <div className="flex items-center gap-2 text-[#F5B841]"><AlertTriangle className="w-4 h-4" /><span className="text-sm font-display font-bold">Not configured</span></div>
            <p className="text-xs text-[#8B97AE]">
              Contract-formation categories are not defined yet for the <span className="font-mono uppercase text-[#F5B841]">{ctx.vertical}</span> vertical.
              This lane is pending legal setup — please check back later.
            </p>
            <div className="pt-2 flex justify-end">
              <button onClick={onClose} className="px-4 h-9 border border-white/20 text-xs font-mono uppercase tracking-widest hover:bg-white/5">Close</button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE] flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-[#2DD4A7]" />
              Vertical <span className="text-[#E8ECF4]">{ctx.vertical}</span>
            </div>

            <div className="border border-white/10 p-3 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-[#8B97AE]">Verified 3-month weighted volume</span>
                <span className="font-mono text-[#E8ECF4]" data-testid="cc-verified">{fmt(ctx.verifiedMonthlyVolume)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[#8B97AE]">Bounds (70%–200%)</span>
                <span className="font-mono text-[#E8ECF4]" data-testid="cc-bounds">{fmt(ctx.minAgreed)} – {fmt(ctx.maxAgreed)}</span>
              </div>
            </div>

            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Where to register</div>
              <div className="space-y-2">
                <label className="block">
                  <div className="text-[11px] text-[#E8ECF4] mb-0.5">Casino / operator <span className="text-[#F87171]">*</span></div>
                  {(ctx.operators && ctx.operators.length > 0) ? (
                    <select
                      data-testid="cc-casino"
                      value={casino}
                      onChange={(e) => setCasino(e.target.value)}
                      className="w-full bg-[#0B1A14] border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono focus:border-[#2DD4A7] outline-none"
                    >
                      <option value="">— select —</option>
                      {ctx.operators.map((op, i) => (
                        <option key={`${op.id || 'x'}-${i}`} value={op.name}>{op.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      data-testid="cc-casino"
                      type="text"
                      value={casino}
                      onChange={(e) => setCasino(e.target.value)}
                      placeholder="e.g. Stake"
                      className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono focus:border-[#2DD4A7] outline-none"
                    />
                  )}
                </label>
                <label className="block">
                  <div className="text-[11px] text-[#E8ECF4] mb-0.5">Affiliate code <span className="text-[#F87171]">*</span></div>
                  <input
                    data-testid="cc-aff-code"
                    type="text"
                    value={affCode}
                    onChange={(e) => setAffCode(e.target.value)}
                    placeholder="e.g. STREAM123"
                    maxLength={64}
                    className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono focus:border-[#2DD4A7] outline-none"
                  />
                </label>
                <label className="block">
                  <div className="text-[11px] text-[#E8ECF4] mb-0.5">Referral link <span className="text-[#8B97AE]">(optional)</span></div>
                  <input
                    data-testid="cc-ref-link"
                    type="url"
                    value={refLink}
                    onChange={(e) => setRefLink(e.target.value)}
                    placeholder="https://…"
                    maxLength={500}
                    className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono focus:border-[#2DD4A7] outline-none"
                  />
                  {!refLinkValid && (
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="cc-ref-link-invalid">Must start with http:// or https://</div>
                  )}
                </label>
                {!affiliateValid && (casino !== "" || affCode !== "") && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="cc-affiliate-required">Casino and affiliate code are required</div>
                )}
              </div>
            </div>

            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Agreed monthly wager per category · fee earned once, 90-day term</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(config.categories || []).map((c) => (
                  <label key={c.key} className="block">
                    <div className="flex items-baseline justify-between mb-0.5">
                      <span className="text-[11px] text-[#E8ECF4]">{CATEGORY_LABELS[c.key] || c.key}</span>
                      <span className="text-[9px] font-mono text-[#8B97AE]">×{Math.round((c.coefficient || 0) * 100)}%</span>
                    </div>
                    <input
                      data-testid={`cc-cat-${c.key}`}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="1"
                      value={cats[c.key] ?? ""}
                      onChange={(e) => setCats((prev) => ({ ...prev, [c.key]: e.target.value }))}
                      placeholder="0"
                      className="w-full bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-2 font-mono focus:border-[#2DD4A7] outline-none"
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="border border-white/10 p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-[#E8ECF4] font-display font-bold">Agreed monthly weighted wager</span>
                <span className="font-mono font-bold text-[#2DD4A7]" data-testid="cc-agreed-weighted">{fmt(agreedWeighted)}</span>
              </div>
              {agreedWeighted > 0 && !inBounds && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="cc-out-of-bounds">
                  Must be between {fmt(ctx.minAgreed)} and {fmt(ctx.maxAgreed)}
                </div>
              )}
            </div>

            {/* Iter 164 — PRIVACY FIX. The "Player rebate (whole deal)"
                tile was removed from this streamer-facing modal (Iter
                147 had shown it here). The player rebate is a VG↔player
                matter — never surfaced to the streamer. The player still
                sees their rebate on their own dashboard (`RebatePreview`),
                on the Player Agreement, and on their settlement view. */}

            <div className="border border-white/10 p-3 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-[#8B97AE]">Chat-unlock already paid</span>
                <span className="font-mono text-[#E8ECF4]">{fmt(ctx.chatUnlockAlreadyPaid)} cr</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[#8B97AE]">Available credits</span>
                <span className="font-mono text-[#E8ECF4]" data-testid="cc-available">{fmt(ctx.availableCredits)} cr</span>
              </div>
              <div className="flex justify-between text-sm pt-1 border-t border-white/10 mt-1.5">
                <span className="text-[#E8ECF4] font-display font-bold">To be reserved</span>
                <span className={`font-mono font-bold ${insufficient ? "text-[#F87171]" : "text-[#2DD4A7]"}`} data-testid="cc-reserve">
                  {quoting ? "…" : reserveAmount == null ? "—" : `${fmt(reserveAmount)} cr`}
                </span>
              </div>
            </div>

            <p className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
              Reserve holds credits on the live contract. They move to consumed only at settlement.
            </p>

            {insufficient && (
              <div className="flex items-center gap-2 text-xs text-[#F87171] border border-[#F87171]/40 bg-[#F87171]/[0.06] p-2" data-testid="cc-insufficient">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{t("cc.insufficient")}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-white/10 sticky bottom-0 bg-[#0B1A14] py-2">
              <button onClick={onClose} className="px-4 h-9 border border-white/20 text-xs font-mono uppercase tracking-widest text-[#8B97AE] hover:text-[#E8ECF4] hover:bg-white/5">{t("common.cancel")}</button>
              <button
                data-testid="cc-submit"
                onClick={submit}
                disabled={!canSubmit}
                className="px-4 h-9 bg-[#2DD4A7] text-[#04150F] text-xs font-mono uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40"
              >{submitting ? "…" : `Reserve & create`}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
